import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import TopBar from '../components/layout/TopBar';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import QRScanner from '../components/ui/QRScanner';
import apiClient from '../utils/apiClient';
import { getExchangeRate } from '../utils/exchangeRates';
import { calculateFees, validateAddress } from '@ikhua/shared';
import { HiShieldCheck } from 'react-icons/hi2';
import {
  signBtcTransaction,
  signTrxTransaction,
  signTrc20Transactions,
} from '../utils/browserSigner';
import { toast } from 'react-toastify';
import { HiExclamationTriangle, HiBolt, HiClock, HiQrCode, HiUser, HiGlobeAlt, HiCheckCircle, HiXCircle } from 'react-icons/hi2';

const API = import.meta.env.VITE_API_URL ?? '';

const INTERNAL_COINS = [
  { key: 'btc',  label: 'Bitcoin', symbol: 'BTC',  icon: '₿', cls: 'btc'  },
  { key: 'usdt', label: 'USDT',    symbol: 'USDT', icon: '$', cls: 'usdt' },
  { key: 'trx',  label: 'TRON',    symbol: 'TRX',  icon: 'T', cls: 'trx'  },
  { key: 'bnb',  label: 'BNB',     symbol: 'BNB',  icon: 'B', cls: 'eth'  },
];

const CHAINS = [
  { key: 'btc',        label: 'Bitcoin',    symbol: 'BTC',  cls: 'btc',  icon: '₿' },
  { key: 'trx',        label: 'USDT TRC20', symbol: 'USDT', cls: 'usdt', icon: '$' },
  { key: 'trx-native', label: 'TRON',       symbol: 'TRX',  cls: 'trx',  icon: 'T' },
  { key: 'bnb',        label: 'BNB',        symbol: 'BNB',  cls: 'eth',  icon: 'B' },
];

export default function Send() {
  const location = useLocation();
  const navigate = useNavigate();
  const { wallet } = useContext(WalletContext);
  const userPickedChain = useRef(!!location.state?.chain);

  // 'null' = show mode picker, 'internal' = Ikhua user flow, 'onchain' = existing flow
  const [mode, setMode] = useState(location.state?.mode ?? null);

  /* ── Internal transfer state ── */
  const [iCoin, setICoin]             = useState('usdt');
  const [iRecipient, setIRecipient]   = useState('');
  const [iLookup, setILookup]         = useState(null);   // { username, displayName } | null
  const [iLookupErr, setILookupErr]   = useState('');
  const [iLookupLoading, setILookupLoading] = useState(false);
  const [iAmount, setIAmount]         = useState('');
  const [iAmountErr, setIAmountErr]   = useState('');
  const [iSending, setISending]       = useState(false);
  const [iSuccess, setISuccess]       = useState(null);   // { recipient, coin, amount } | null

  /* Debounced recipient lookup */
  useEffect(() => {
    setILookup(null);
    setILookupErr('');
    const q = iRecipient.trim();
    if (!q || q.length < 2) return;
    const t = setTimeout(async () => {
      setILookupLoading(true);
      try {
        const r = await fetch(`${API}/api/wallet/lookup-user?q=${encodeURIComponent(q)}&callerEmail=${encodeURIComponent(wallet?.email ?? '')}`);
        const d = await r.json();
        if (!r.ok) setILookupErr(d.error ?? 'User not found');
        else setILookup(d);
      } catch { setILookupErr('Lookup failed'); }
      finally { setILookupLoading(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [iRecipient]);

  /* Validate internal amount */
  useEffect(() => {
    if (!iAmount) { setIAmountErr(''); return; }
    const num = parseFloat(iAmount);
    if (isNaN(num) || num <= 0) { setIAmountErr('Enter a valid amount'); return; }
    const bal = wallet?.balances?.[iCoin] ?? 0;
    if (num > bal) setIAmountErr(`Insufficient balance (you have ${bal} ${iCoin.toUpperCase()})`);
    else setIAmountErr('');
  }, [iAmount, iCoin, wallet?.balances]);

  const doInternalTransfer = async () => {
    if (!iLookup || !iAmount || iAmountErr) return;
    setISending(true);
    try {
      const r = await fetch(`${API}/api/wallet/internal-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromEmail: wallet.email, toUsername: iRecipient.trim(), coin: iCoin, amount: parseFloat(iAmount) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setISuccess({ recipient: d.recipient, coin: iCoin, amount: parseFloat(iAmount) });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setISending(false);
    }
  };

  const [activeChain, setActiveChain]     = useState(location.state?.chain ?? 'btc');
  const [balances, setBalances]           = useState({ btc: null, trx: null, 'trx-native': null, bnb: null });
  const [balancesFetched, setBalancesFetched] = useState({ btc: false, trx: false, 'trx-native': false, bnb: false });
  const [btcRate, setBtcRate]             = useState(1);
  const [recipient, setRecipient]     = useState('');
  const [amount, setAmount]           = useState('');
  const [amountInBtc, setAmountInBtc] = useState(true); // true = BTC entry, false = USD entry
  const [amountError, setAmountError] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [feeSpeed, setFeeSpeed]       = useState('medium');
  const [fees, setFees]               = useState(null);
  const [feeLoading, setFeeLoading]   = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending]         = useState(false);
  const [scanOpen, setScanOpen]       = useState(false);
  const [sendMinUsd, setSendMinUsd]   = useState(100);
  const [subtractFee, setSubtractFee] = useState(false);

  const chain   = CHAINS.find(c => c.key === activeChain);
  const addrMap = {
    btc:          wallet?.wallets?.btc?.address  || wallet?.btcAddress,
    trx:          wallet?.wallets?.trx?.address  || wallet?.trxAddress,
    'trx-native': wallet?.wallets?.trx?.address  || wallet?.trxAddress,
    bnb:          wallet?.wallets?.bnb?.address  || wallet?.ethAddress,
  };
  const address = addrMap[activeChain] ?? '';
  const balance = balances[activeChain];

  /* Fetch platform withdrawal minimum on mount */
  useEffect(() => {
    apiClient.get('/p2p/limits').then(r => { if (r.data?.sendMinUsd) setSendMinUsd(r.data.sendMinUsd); }).catch(() => {});
  }, []);

  /* Fetch all balances + BTC rate on mount */
  useEffect(() => {
    const addrFor = {
      'btc':        wallet?.wallets?.btc?.address  || wallet?.btcAddress,
      'trx':        wallet?.wallets?.trx?.address  || wallet?.trxAddress,
      'trx-native': wallet?.wallets?.trx?.address  || wallet?.trxAddress,
      'bnb':        wallet?.wallets?.bnb?.address  || wallet?.ethAddress,
    };
    CHAINS.forEach(({ key }) => {
      const addr = addrFor[key];
      if (!addr) {
        setBalancesFetched(prev => ({ ...prev, [key]: true }));
        setBalances(prev => ({ ...prev, [key]: 0 }));
        return;
      }
      apiClient.get(`/wallet/balance/${key}/${addr}`)
        .then(r => {
          setBalances(prev => ({ ...prev, [key]: Number(r.data.balance) ?? 0 }));
          setBalancesFetched(prev => ({ ...prev, [key]: true }));
        })
        .catch(() => {
          setBalancesFetched(prev => ({ ...prev, [key]: true }));
        });
    });
    getExchangeRate('bitcoin').then(r => setBtcRate(r || 1));
  }, [wallet]);

  /* Auto-select highest funded coin (only if user didn't navigate here with a specific chain) */
  useEffect(() => {
    if (userPickedChain.current) return;
    if (CHAINS.every(c => balances[c.key] === null)) return;

    const usdValues = {
      'btc':        (balances['btc']        || 0) * btcRate,
      'trx':         balances['trx']        || 0,
      'trx-native': (balances['trx-native'] || 0) * 0.12,
      'bnb':        (balances['bnb']        || 0) * 300,
    };
    const best = Object.entries(usdValues).sort((a, b) => b[1] - a[1])[0][0];
    if (usdValues[best] > 0) setActiveChain(best);
  }, [balances, btcRate]);

  /* Real-time balance validation */
  useEffect(() => {
    if (!amount) { setAmountError(''); return; }
    if (!balancesFetched[activeChain]) return; // still loading
    const num = Number(amount);
    if (num <= 0) { setAmountError('Enter a valid amount'); return; }

    if (activeChain === 'btc') {
      const amountBTC = amountInBtc ? num : num / btcRate;
      const amountUsd = amountBTC * btcRate;
      const estFeeUsd = fees?.platformFee ?? amountUsd * 0.03;
      const totalUsd  = subtractFee ? amountUsd : amountUsd + estFeeUsd;
      if (subtractFee ? amountBTC > balance : (amountBTC + amountBTC * 0.03) > balance) {
        setAmountError(`Insufficient balance (you have ${balance.toFixed(6)} BTC ≈ $${(balance * btcRate).toFixed(2)})`);
      } else if (!subtractFee && totalUsd < sendMinUsd) {
        setAmountError(`Minimum withdrawal is $${sendMinUsd} USD equivalent`);
      } else {
        setAmountError('');
      }
    } else {
      const estFeeRate = 0.03;
      const totalNeeded = subtractFee ? num : num * (1 + estFeeRate);
      if (totalNeeded > balance) {
        const dp = activeChain === 'bnb' ? 6 : 2;
        setAmountError(
          subtractFee
            ? `Insufficient balance (you have ${balance.toFixed(dp)} ${chain.symbol})`
            : `Insufficient balance to cover amount + fee (you have ${balance.toFixed(dp)} ${chain.symbol})`
        );
      } else {
        setAmountError('');
      }
    }
  }, [amount, balance, activeChain, btcRate, chain.symbol, amountInBtc, balancesFetched, sendMinUsd, subtractFee]);

  /* Fee calculation */
  const loadFees = useCallback(async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0 || amountError) { setFees(null); return; }
    setFeeLoading(true);
    try {
      const f = await calculateFees(Number(amount), activeChain, { btc: btcRate, trx: 1, bnb: 300 }, { btcFeeRate: feeSpeed });
      setFees(f);
    } catch { setFees(null); }
    finally { setFeeLoading(false); }
  }, [amount, activeChain, btcRate, feeSpeed, amountError]);

  useEffect(() => { loadFees(); }, [loadFees]);

  /* Max button — when fee from balance, leave room for fee; when fee from amount, use full balance */
  const setMax = () => {
    if (typeof balance !== 'number' || balance <= 0) return;
    if (activeChain === 'btc') {
      // subtractFee=true: fee inside the amount → full balance; false: fee on top → leave ~3% headroom
      const maxBtc = subtractFee ? balance : balance / 1.03;
      setAmount(amountInBtc ? maxBtc.toFixed(8) : (maxBtc * btcRate).toFixed(2));
    } else {
      // subtractFee=true: fee inside → full balance; false: fee on top → leave ~3% for fee
      const maxAmt = subtractFee ? balance : balance / 1.03;
      setAmount(maxAmt.toFixed(activeChain === 'bnb' ? 8 : 6));
    }
  };

  const switchChain = (key) => {
    userPickedChain.current = true;
    setActiveChain(key);
    setFees(null);
    setAmount('');
    setAmountError('');
    setRecipientError('');
    setSubtractFee(false);
  };

  const onReview = () => {
    let hasError = false;
    if (!recipient) { setRecipientError('Address is required'); hasError = true; }
    else if (!validateAddress(recipient, activeChain)) { setRecipientError('Invalid address for this network'); hasError = true; }
    else setRecipientError('');
    if (amountError || !amount) hasError = true;
    if (!hasError) setConfirmOpen(true);
  };

  const onConfirm = async () => {
    setSending(true);
    try {
      const fromAddress = address;
      let txid;

      if (wallet?.custodial) {
        /* ── Custodial: backend signs and broadcasts ──────────── */
        const body = { email: wallet.email, coin: activeChain, toAddress: recipient, feeSpeed };
        if (subtractFee) body.subtractFee = true;
        if (activeChain === 'btc') {
          const btcAmount = amountInBtc ? Number(amount) : Number(amount) / btcRate;
          body.amountSats = Math.floor(btcAmount * 1e8);
        } else {
          body.amount = Number(amount);
        }
        const { data: result } = await apiClient.post('/wallet/send', body);
        txid = result.txid;
      } else {
        /* ── Legacy: client signs, backend broadcasts ─────────── */
        const mnemonic = wallet?.mnemonic;
        if (!mnemonic) throw new Error('Recovery phrase not found. Please re-import your wallet using your seed phrase.');

        const buildBody = { coin: activeChain, fromAddress, toAddress: recipient, feeSpeed };
        if (activeChain === 'btc') {
          const btcAmount = amountInBtc ? Number(amount) : Number(amount) / btcRate;
          buildBody.amountSats = Math.floor(btcAmount * 1e8);
          if (subtractFee) buildBody.subtractFee = true;
        } else {
          buildBody.amount = Number(amount);
        }

        const { data: params } = await apiClient.post('/wallet/build-tx', buildBody);

        let signedTx = null, signedTxs = null;
        if (activeChain === 'btc') {
          signedTx = signBtcTransaction({ mnemonic, utxos: params.utxos, toAddress: recipient, amountSats: buildBody.amountSats, platformFeeAddress: params.platformFeeAddress, platformFeeSats: params.platformFeeSats, feeRateSatVbyte: params.feeRateSatVbyte, fromAddress, subtractFee: !!params.subtractFee });
        } else if (activeChain === 'trx-native') {
          signedTx = await signTrxTransaction({ mnemonic, unsignedTx: params.unsignedTx });
          if (params.feeUnsignedTx) signedTxs = { feeTx: await signTrxTransaction({ mnemonic, unsignedTx: params.feeUnsignedTx }) };
        } else if (activeChain === 'trx') {
          signedTxs = await signTrc20Transactions({ mnemonic, unsignedTxs: params.unsignedTxs });
        }

        const { data: result } = await apiClient.post('/wallet/broadcast', { coin: activeChain, signedTx, signedTxs });
        txid = result.txid;
      }

      toast.success(`Sent! TX: ${txid?.slice(0, 14)}…`);
      navigate('/home');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Transaction failed');
      setSending(false);
    }
  };

  const canSend = amount && !amountError && recipient && !recipientError;
  const kycApproved = wallet?.kycStatus === 'approved';

  /* ── KYC wall — applies to both internal and on-chain sends. Internal
     transfers move real value between Ikhua wallets, so they get the same
     gate as on-chain withdrawals rather than being a KYC-exempt escape hatch. */
  if (!kycApproved) {
    const isPending  = wallet?.kycStatus === 'pending';
    const isRejected = wallet?.kycStatus === 'rejected';
    return (
      <div>
        <TopBar title="Send" back />
        <div style={{ margin: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 20 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: isPending ? 'rgba(245,158,11,0.12)' : isRejected ? 'rgba(239,68,68,0.12)' : 'rgba(124,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HiShieldCheck size={36} color={isPending ? '#F59E0B' : isRejected ? '#EF4444' : '#7C5CF6'} />
          </div>
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 800, color: '#fff' }}>
              {isPending ? 'KYC Under Review' : isRejected ? 'KYC Rejected' : 'Verify Your Identity First'}
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#8B8FA8', lineHeight: 1.6, maxWidth: 300 }}>
              {isPending
                ? 'Your KYC is being reviewed. Sending — including free transfers to other Ikhua users — will be unlocked once approved.'
                : isRejected
                ? 'Your verification was rejected. Re-submit to unlock sending.'
                : 'We need to verify your identity before you can send funds, including free transfers to other Ikhua users.'}
            </p>
          </div>
          {!isPending && (
            <button onClick={() => navigate('/profile')} style={{ padding: '14px 32px', borderRadius: 14, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #7C5CF6, #5B3FD9)', color: '#fff', fontSize: '15px', fontWeight: 700 }}>
              {isRejected ? 'Re-submit Documents' : 'Start Verification'}
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ── Mode picker ─────────────────────────────────────────────────── */
  if (!mode) {
    return (
      <div>
        <TopBar title="Send" back />
        <div style={{ padding: '24px 20px' }}>
          <p style={{ fontSize: '13px', color: '#8B8FA8', margin: '0 0 24px', textAlign: 'center' }}>
            How do you want to send?
          </p>

          {/* To Ikhua User */}
          <button
            onClick={() => setMode('internal')}
            style={{ width: '100%', padding: '20px', marginBottom: 14, borderRadius: 18, border: '1px solid rgba(124,92,246,0.35)', background: 'rgba(124,92,246,0.08)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16 }}
          >
            <div style={{ width: 50, height: 50, borderRadius: 14, background: 'rgba(124,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HiUser size={24} color="#A78BFA" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#fff', marginBottom: 4 }}>Send to Ikhua User</div>
              <div style={{ fontSize: '13px', color: '#8B8FA8', lineHeight: 1.5 }}>
                Transfer to another Ikhua wallet by @username or email. Instant and <span style={{ color: '#10B981', fontWeight: 700 }}>completely free</span>.
              </div>
            </div>
            <div style={{ background: '#10B98122', color: '#10B981', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: 20, flexShrink: 0 }}>FREE</div>
          </button>

          {/* On-chain */}
          <button
            onClick={() => setMode('onchain')}
            style={{ width: '100%', padding: '20px', borderRadius: 18, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16 }}
          >
            <div style={{ width: 50, height: 50, borderRadius: 14, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HiGlobeAlt size={24} color="#8B8FA8" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#fff', marginBottom: 4 }}>Send On-chain</div>
              <div style={{ fontSize: '13px', color: '#8B8FA8', lineHeight: 1.5 }}>
                Withdraw to any external wallet address on BTC, USDT TRC20, TRX, or BNB. Network fees apply.
              </div>
            </div>
            <div style={{ background: '#EF444422', color: '#EF4444', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: 20, flexShrink: 0 }}>FEE</div>
          </button>
        </div>
      </div>
    );
  }

  /* ── Internal transfer flow ────────────────────────────────────────── */
  if (mode === 'internal') {
    const iCoinMeta = INTERNAL_COINS.find(c => c.key === iCoin);
    const iBalance  = wallet?.balances?.[iCoin] ?? 0;

    if (iSuccess) {
      return (
        <div>
          <TopBar title="Transfer Sent" back={() => { setISuccess(null); setMode(null); setIRecipient(''); setIAmount(''); setILookup(null); }} />
          <div style={{ padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, textAlign: 'center' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HiCheckCircle size={44} color="#10B981" />
            </div>
            <div>
              <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>Transfer Complete</h2>
              <p style={{ fontSize: '14px', color: '#8B8FA8', margin: 0, lineHeight: 1.6 }}>
                {iSuccess.amount} {iSuccess.coin.toUpperCase()} sent to{' '}
                <strong style={{ color: '#A78BFA' }}>
                  {iSuccess.recipient?.username ? `@${iSuccess.recipient.username}` : iSuccess.recipient?.displayName}
                </strong>
                {' '}— free of charge.
              </p>
            </div>
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 14, padding: '14px 20px', width: '100%', maxWidth: 320 }}>
              <div style={{ fontSize: '12px', color: '#8B8FA8', marginBottom: 4 }}>Fee charged</div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#10B981' }}>$0.00</div>
            </div>
            <button
              onClick={() => { setISuccess(null); setMode(null); setIRecipient(''); setIAmount(''); setILookup(null); navigate('/home'); }}
              style={{ padding: '14px 40px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg,#7C5CF6,#5B3FD9)', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <TopBar title="Send to Ikhua User" back={() => setMode(null)} />

        {/* Coin tabs */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 20px 20px', overflowX: 'auto' }}>
          {INTERNAL_COINS.map(c => (
            <button key={c.key} onClick={() => { setICoin(c.key); setIAmount(''); setIAmountErr(''); }}
              style={{ flexShrink: 0, padding: '8px 16px', borderRadius: 20, border: '1px solid', borderColor: iCoin === c.key ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)', background: iCoin === c.key ? 'rgba(124,92,246,0.12)' : 'transparent', color: iCoin === c.key ? '#7C5CF6' : '#8B8FA8', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              {c.symbol}
            </button>
          ))}
        </div>

        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Balance */}
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#8B8FA8' }}>Your {iCoinMeta?.symbol} balance</span>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{Number(iBalance).toFixed(iCoin === 'btc' ? 8 : 4)} {iCoinMeta?.symbol}</span>
          </div>

          {/* Recipient */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#4B5170', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 8 }}>
              Recipient
            </label>
            <div style={{ position: 'relative' }}>
              <input
                value={iRecipient}
                onChange={e => setIRecipient(e.target.value)}
                placeholder="@username or email"
                style={{ width: '100%', height: 48, padding: '0 16px', borderRadius: 13, border: `1px solid ${iLookupErr ? '#EF4444' : iLookup ? '#10B981' : 'rgba(255,255,255,0.1)'}`, background: '#110E30', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
              {iLookupLoading && (
                <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#8B8FA8' }}>…</div>
              )}
              {iLookup && !iLookupLoading && (
                <HiCheckCircle size={18} color="#10B981" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }} />
              )}
              {iLookupErr && !iLookupLoading && (
                <HiXCircle size={18} color="#EF4444" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
            {iLookup && (
              <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(124,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>👤</div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{iLookup.displayName || iLookup.username}</div>
                  {iLookup.username && <div style={{ fontSize: '11px', color: '#8B8FA8' }}>@{iLookup.username}</div>}
                </div>
                {iLookup.kycApproved && <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#10B981', fontWeight: 700 }}>✓ KYC</span>}
              </div>
            )}
            {iLookupErr && <div style={{ fontSize: '12px', color: '#EF4444', marginTop: 6 }}>{iLookupErr}</div>}
          </div>

          {/* Amount */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#4B5170', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 8 }}>
              Amount ({iCoinMeta?.symbol})
            </label>
            <input
              value={iAmount}
              onChange={e => setIAmount(e.target.value)}
              placeholder={`0.00 ${iCoinMeta?.symbol}`}
              type="number"
              min="0"
              step="any"
              style={{ width: '100%', height: 48, padding: '0 16px', borderRadius: 13, border: `1px solid ${iAmountErr ? '#EF4444' : 'rgba(255,255,255,0.1)'}`, background: '#110E30', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
            />
            {iAmountErr && <div style={{ fontSize: '12px', color: '#EF4444', marginTop: 6 }}>{iAmountErr}</div>}
            <button onClick={() => setIAmount(String(iBalance))} style={{ background: 'none', border: 'none', color: '#7C5CF6', fontSize: '12px', cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}>
              Send max
            </button>
          </div>

          {/* Fee disclosure */}
          <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 13, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 16 }}>🎉</span>
            <span style={{ fontSize: '12px', color: '#10B981', lineHeight: 1.5 }}>
              <strong>Zero fees.</strong> Transfers between Ikhua wallets are instant and completely free — no network gas, no platform fee, no minimum.
            </span>
          </div>

          {/* Send button */}
          <button
            onClick={doInternalTransfer}
            disabled={iSending || !iLookup || !iAmount || !!iAmountErr}
            style={{ width: '100%', height: 52, borderRadius: 14, border: 'none', background: iSending || !iLookup || !iAmount || !!iAmountErr ? 'rgba(124,92,246,0.3)' : 'linear-gradient(135deg,#7C5CF6,#5B3FD9)', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: iSending || !iLookup || !iAmount || !!iAmountErr ? 'default' : 'pointer' }}
          >
            {iSending ? 'Sending…' : `Send ${iAmount || '0'} ${iCoinMeta?.symbol} Free`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Send" back={() => setMode(null)} />

      {/* Chain selector */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 20px 16px', overflowX: 'auto' }}>
        {CHAINS.map(c => {
          const bal    = balances[c.key];
          const funded = typeof bal === 'number' && bal > 0;
          return (
            <button key={c.key} onClick={() => switchChain(c.key)} style={{
              flexShrink: 0, padding: '8px 16px', borderRadius: 20,
              border: `1px solid ${activeChain === c.key ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
              background: activeChain === c.key ? 'rgba(124,92,246,0.12)' : 'transparent',
              color: activeChain === c.key ? '#7C5CF6' : '#8B8FA8',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s ease',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {c.label}
              {funded ? <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', flexShrink: 0 }} /> : null}
            </button>
          );
        })}
      </div>

      {/* Balance */}
      <div style={{ margin: '0 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1A1748', borderRadius: 14, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className={`coin-icon ${chain.cls}`} style={{ width: 32, height: 32, borderRadius: 10, fontSize: 14 }}>{chain.icon}</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{chain.symbol}</div>
            <div style={{ fontSize: '11px', color: '#4B5170' }}>{chain.label}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
              {!balancesFetched[activeChain]
                ? '…'
                : `${(balance ?? 0).toFixed(activeChain === 'btc' ? 6 : 2)} ${chain.symbol}`}
            </div>
            <div style={{ fontSize: '11px', color: '#4B5170' }}>Available</div>
          </div>
          <button onClick={setMax} disabled={!balancesFetched[activeChain] || !(balance > 0)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            background: 'rgba(124,92,246,0.15)', border: '1px solid rgba(124,92,246,0.3)',
            color: '#7C5CF6', opacity: (balance > 0) ? 1 : 0.4,
          }}>
            Max
          </button>
        </div>
      </div>

      {/* Form */}
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          label="Recipient address"
          placeholder={`Enter ${chain.label} address`}
          value={recipient}
          onChange={e => { setRecipient(e.target.value); setRecipientError(''); }}
          error={recipientError}
          right={
            <button
              onClick={() => setScanOpen(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: '#7C5CF6' }}
              title="Scan QR code"
            >
              <HiQrCode size={22} />
            </button>
          }
        />

        {/* Amount input with BTC/USD toggle */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#8B8FA8' }}>
              {activeChain === 'btc' ? (amountInBtc ? 'Amount (BTC)' : 'Amount (USD)') : `Amount (${chain.symbol})`}
            </span>
            {activeChain === 'btc' && (
              <button
                onClick={() => { setAmountInBtc(b => !b); setAmount(''); setAmountError(''); }}
                style={{ display: 'flex', background: '#1A1748', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, overflow: 'hidden', cursor: 'pointer' }}
              >
                {['BTC', 'USD'].map(unit => (
                  <span key={unit} style={{
                    padding: '4px 12px', fontSize: '11px', fontWeight: 700,
                    background: (unit === 'BTC') === amountInBtc ? '#7C5CF6' : 'transparent',
                    color:      (unit === 'BTC') === amountInBtc ? '#fff' : '#4B5170',
                    transition: 'all 0.15s',
                  }}>{unit}</span>
                ))}
              </button>
            )}
          </div>
          <Input
            type="text"
            inputMode="decimal"
            placeholder={activeChain === 'btc' ? (amountInBtc ? 'e.g. 0.00084733' : 'e.g. 50.00') : '0.00'}
            value={amount}
            onChange={e => {
              const clean = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
              setAmount(clean);
            }}
            error={amountError}
            hint={
              activeChain === 'btc' && amount && !amountError
                ? amountInBtc
                  ? `≈ $${(Number(amount) * btcRate).toFixed(2)} USD`
                  : `≈ ${(Number(amount) / btcRate).toFixed(8)} BTC`
                : ''
            }
          />
        </div>

        {/* Fee mode toggle — all coins */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 14px', background: subtractFee ? 'rgba(124,92,246,0.08)' : 'rgba(255,255,255,0.03)', borderRadius: 12, border: `1px solid ${subtractFee ? 'rgba(124,92,246,0.3)' : 'rgba(255,255,255,0.07)'}`, transition: 'all 0.15s' }}>
          <input
            type="checkbox"
            checked={subtractFee}
            onChange={e => setSubtractFee(e.target.checked)}
            style={{ marginTop: 2, accentColor: '#7C5CF6', width: 16, height: 16, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: subtractFee ? '#7C5CF6' : '#8B8FA8' }}>Deduct fee from send amount</div>
            <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 2 }}>
              {subtractFee
                ? 'Recipient gets less · Your balance covers only the send amount'
                : 'Recipient gets the full amount · Fee charged from your balance'}
            </div>
          </div>
        </label>

        {/* Fee speed — only BTC has meaningful speed options */}
        {activeChain === 'btc' && (
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#8B8FA8', marginBottom: 8 }}>Network speed</p>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { key: 'fast',   label: 'Fast',     icon: <HiBolt size={14} /> },
                { key: 'medium', label: 'Standard', icon: <HiClock size={14} /> },
                { key: 'slow',   label: 'Economy',  icon: <HiClock size={14} /> },
              ].map(opt => (
                <button key={opt.key} onClick={() => setFeeSpeed(opt.key)} style={{
                  flex: 1, padding: '10px 0', borderRadius: 12,
                  border: `1px solid ${feeSpeed === opt.key ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
                  background: feeSpeed === opt.key ? 'rgba(124,92,246,0.1)' : 'transparent',
                  color: feeSpeed === opt.key ? '#7C5CF6' : '#8B8FA8',
                  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                }}>
                  {opt.icon}{opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Gas info banners */}
        {activeChain === 'trx' && (
          <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 12, padding: '10px 14px', display: 'flex', gap: 8 }}>
            <span style={{ color: '#10B981', fontSize: 14, flexShrink: 0 }}>⛽</span>
            <span style={{ fontSize: '12px', color: '#10B981', lineHeight: 1.5 }}>
              Network gas is handled by Ikhua — no TRX needed. Gas cost is included in your fee.
            </span>
          </div>
        )}
        {activeChain === 'trx-native' && (
          <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 12, padding: '10px 14px', display: 'flex', gap: 8 }}>
            <span style={{ color: '#10B981', fontSize: 14, flexShrink: 0 }}>⛽</span>
            <span style={{ fontSize: '12px', color: '#10B981', lineHeight: 1.5 }}>
              TRX network fees are very low (~1 TRX). Network gas is covered by Ikhua.
            </span>
          </div>
        )}
        {activeChain === 'bnb' && (
          <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 12, padding: '10px 14px', display: 'flex', gap: 8 }}>
            <span style={{ color: '#10B981', fontSize: 14, flexShrink: 0 }}>⛽</span>
            <span style={{ fontSize: '12px', color: '#10B981', lineHeight: 1.5 }}>
              BNB Smart Chain gas is very low (~$0.05–0.30) and covered by the platform.
            </span>
          </div>
        )}

        {feeLoading && <div style={{ textAlign: 'center', color: '#4B5170', fontSize: '13px' }}>Calculating fees…</div>}
        {fees && !feeLoading && (
          <div style={{ background: '#1A1748', borderRadius: 14, padding: '14px' }}>
            <FeeRow label="Platform fee" value={`$${fees.platformFee.toFixed(2)}`} />
            <FeeRow label="Network fee"  value={`≈ $${fees.networkFeeUsd.toFixed(4)}`} />
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '8px 0' }} />
            {subtractFee ? (() => {
              if (activeChain === 'btc' && amount && !amountError) {
                const amtBtc       = amountInBtc ? Number(amount) : Number(amount) / btcRate;
                const totalFeesBtc = fees.totalFeeUsd / btcRate;
                const recipientBtc = Math.max(0, amtBtc - totalFeesBtc);
                return <FeeRow label="Recipient gets ≈" value={`${recipientBtc.toFixed(6)} BTC ($${(recipientBtc * btcRate).toFixed(2)})`} bold />;
              }
              return <FeeRow label="Recipient gets ≈" value={`${Number(amount) > 0 ? (Number(amount) - fees.platformFee).toFixed(activeChain === 'bnb' ? 8 : 6) : '—'} ${chain.symbol}`} bold />;
            })() : (
              <FeeRow label="You pay total ≈" value={`$${(Number(amount || 0) * (activeChain === 'btc' ? btcRate : activeChain === 'bnb' ? 300 : 1) + fees.totalFeeUsd).toFixed(2)}`} bold />
            )}
          </div>
        )}

        <Button variant="primary" size="lg" full onClick={onReview} disabled={!canSend}>
          Review Send
        </Button>
      </div>

      <QRScanner
        open={scanOpen}
        onScan={(address) => { setRecipient(address); setRecipientError(''); setScanOpen(false); }}
        onClose={() => setScanOpen(false)}
      />

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Confirm Transaction">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ConfirmRow label="Sending" value={
            activeChain === 'btc'
              ? amountInBtc
                ? `${amount} BTC (≈ $${(Number(amount) * btcRate).toFixed(2)} USD)`
                : `$${amount} USD (≈ ${(Number(amount) / btcRate).toFixed(8)} BTC)`
              : `${amount} ${chain.symbol}`
          } />
          <ConfirmRow label="Network" value={chain.label} />
          <ConfirmRow label="To"      value={recipient} mono />
          {fees && <ConfirmRow label="Total fees" value={`$${fees.totalFeeUsd.toFixed(4)}`} />}
          <div className="warning-banner">
            <HiExclamationTriangle size={18} style={{ color: '#F59E0B', flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: '#F59E0B', lineHeight: 1.5 }}>
              Crypto transactions are irreversible. Double-check the address and network.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" size="md" full onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="primary"   size="md" full onClick={onConfirm} loading={sending}>Confirm</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function FeeRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
      <span style={{ fontSize: '13px', color: '#8B8FA8' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: bold ? 700 : 500, color: bold ? '#fff' : '#8B8FA8' }}>{value}</span>
    </div>
  );
}

function ConfirmRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px', background: '#1A1748', borderRadius: 12 }}>
      <span style={{ fontSize: '11px', fontWeight: 600, color: '#4B5170', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontSize: mono ? '12px' : '14px', fontWeight: 600, color: '#fff', wordBreak: 'break-all', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  );
}
